import React, { useEffect, useState } from 'react';
import { Table, Radio, AutoComplete, Input, Button } from 'antd';
import Icon from '@ant-design/icons';
import { ReactComponent as OverviewIcon } from '../../static/overview.svg';
import { ReactComponent as VarianceIcon } from '../../static/variance.svg';
import { ReactComponent as DeviceSvg } from '../../static/device.svg';
import { ReactComponent as MeterSvg } from '../../static/meter.svg';
import { useDispatch, useSelector } from 'react-redux';
import { getInverterList, getInverterSummary } from '../../modules/analytics';
import './devicesTable.css';

const formatEnergy = (val) => {
  const num = Number(val) || 0;
  if (num >= 1000) {
    return `${(num / 1000).toFixed(3)}MWh`;
  }
  return `${num.toFixed(3)}KWh`;
};

const formatPercent = (val) => `${Math.round(Number(val) || 0)}%`;

const deltaToPercent = (obj) => {
  if (!obj || obj.delta == null || obj.value == null) return null;
  const prev = Number(obj.value) - Number(obj.delta);
  if (!prev) return 0;
  return (Number(obj.delta) / prev) * 100;
};

const formatLastUpdate = (val) => {
  if (!val) return '';
  const diff = Date.now() - Number(val);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) {
    return `${minutes} min ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days} day ago`;
};

const renderStatus = (status) => (
  <span style={{ color: status === 1 ? 'var(--primary-color)' : '#F00' }}>
    {status === 1 ? 'Online' : 'Offline'}
  </span>
);

const getColumns = (view) => {
  switch (view) {
    case 'variance':
      return [
        {
          title: '',
          dataIndex: 'model',
          key: 'deviceIcon',
          className: 'column-device-icon',
          render: () => <Icon component={MeterSvg} className="deviceIcon" />,
        },
        { title: 'Device', dataIndex: 'sn', key: 'sn' },
        { title: 'Model', dataIndex: 'model', key: 'model' },
        {
          title: 'Change in Generation (24h)',
          dataIndex: 'delta24h',
          key: 'delta24h',
          render: formatPercent,
        },
        {
          title: 'Change in Generation (7d)',
          dataIndex: 'delta7d',
          key: 'delta7d',
          render: formatPercent,
        },
        {
          title: 'Change in Generation (1mo)',
          dataIndex: 'delta1m',
          key: 'delta1m',
          render: formatPercent,
        },
        {
          title: 'Last Updated',
          dataIndex: 'lastUpdate',
          key: 'lastUpdate',
          render: formatLastUpdate,
        },
        { title: 'Status', dataIndex: 'status', key: 'status', render: renderStatus },
        { title: 'Warning', dataIndex: 'warning', key: 'warning' },
        { title: 'Export', dataIndex: 'export', key: 'export' },
      ];
    case 'overview':
    default:
      return [
        {
          title: '',
          dataIndex: 'model',
          key: 'deviceIcon',
          className: 'column-device-icon',
          render: () => <Icon component={MeterSvg} className="deviceIcon" />,
        },
        { title: 'Device', dataIndex: 'sn', key: 'sn' },
        { title: 'Model', dataIndex: 'model', key: 'model' },
        {
          title: 'Generation Today',
          dataIndex: 'generationToday',
          key: 'generationToday',
          render: formatEnergy,
        },
        {
          title: 'Generation Lifetime',
          dataIndex: 'generationLifetime',
          key: 'generationLifetime',
          render: formatEnergy,
        },
        {
          title: 'Current Power Loading',
          dataIndex: 'currentPower',
          key: 'currentPower',
        },
        {
          title: 'Last Updated',
          dataIndex: 'lastUpdate',
          key: 'lastUpdate',
          render: formatLastUpdate,
        },
        { title: 'Status', dataIndex: 'status', key: 'status', render: renderStatus },
        { title: 'Warning', dataIndex: 'warning', key: 'warning' },
        { title: 'Export', dataIndex: 'export', key: 'export' },
      ];
  }
};

const DevicesTable = ({ stationId }) => {
  const dispatch = useDispatch();
  const [view, setView] = useState('overview');
  const [filter, setFilter] = useState('all');
  const [snQuery, setSnQuery] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const list = useSelector(
    (state) => (state.analytics.inverterList || {})[stationId] || []
  );
  const summaryCache = useSelector((state) => state.analytics.inverterSummary || {});
  const summaries = summaryCache[stationId] || {};

  // Enrich list with real-time summaries (status, lastUpdate)
  const enrichedList = list.map(d => {
    const summary = summaries[d.inverterId] || {};
    return {
      ...d,
      status: summary.status ?? d.status,
      lastUpdate: summary.lastUpdate
    };
  });

  const onlineCount = enrichedList.filter((d) => d.status === 1).length;
  const offlineCount = enrichedList.filter((d) => d.status !== 1).length;
  const warningCount = enrichedList.filter((d) => d.warning).length;
  const snOptions = enrichedList.map((d) => ({ value: d.sn }));

  const filteredList = enrichedList
    .filter((d) => {
      if (filter === 'online') return d.status === 1;
      if (filter === 'offline') return d.status !== 1;
      if (filter === 'warning') return !!d.warning;
      return true;
    })
    .filter((d) =>
      snQuery ? String(d.sn || '').toLowerCase().includes(snQuery.toLowerCase()) : true
    );

  useEffect(() => {
    if (stationId) {
      dispatch(getInverterList(stationId));
    }
  }, [dispatch, stationId]);

  useEffect(() => {
    let active = true;
    const ids = list
      .map((inv) => inv.inverterId)
      .filter((id) => !summaries[id]);
    const fetch = async () => {
      for (let i = 0; i < ids.length && active; i += 10) {
        const batch = ids.slice(i, i + 10);
        await Promise.all(
          batch.map((id) => dispatch(getInverterSummary(stationId, id)))
        );
      }
    };
    if (stationId && ids.length > 0) {
      fetch();
    }
    return () => {
      active = false;
    };
  }, [dispatch, list]);

  const dataSource = filteredList.map((d) => {
    const summary = summaries[d.inverterId] || {};
    return {
      ...d,
      generationToday: summary.daily?.value,
      generationLifetime: summary.lifetime,
      currentPower: summary.currentPower,
      lastUpdate: summary.lastUpdate,
      status: summary.status,
      delta24h: deltaToPercent(summary.daily),
      delta7d: deltaToPercent(summary.weekly),
      delta1m: deltaToPercent(summary.monthly),
    };
  });

  const columns = getColumns(view);

  return (
    <div className="devicesTableContainer">
      <div className="tableHeader">
        <Radio.Group
          className="tableControls"
          value={view}
          onChange={(e) => setView(e.target.value)}
        >
          <Radio.Button value="overview">
            <Icon component={OverviewIcon} className="tabIcon" />
            Overview
          </Radio.Button>
          <Radio.Button value="variance">
            <Icon component={VarianceIcon} className="tabIcon" />
            Variance
          </Radio.Button>
        </Radio.Group>
        <div className="tableHeaderRight">
            <Button
              type={filter === 'all' ? 'primary' : 'default'}
              onClick={() => setFilter('all')}
            >
              All ({list.length})
            </Button>
            <Button
              type={filter === 'online' ? 'primary' : 'default'}
              onClick={() => setFilter('online')}
            >
              Online ({onlineCount})
            </Button>
            <Button
              type={filter === 'offline' ? 'primary' : 'default'}
              onClick={() => setFilter('offline')}
            >
              Offline ({offlineCount})
            </Button>
            <Button
              type={filter === 'warning' ? 'primary' : 'default'}
              onClick={() => setFilter('warning')}
            >
              Warning ({warningCount})
            </Button>
          <AutoComplete
            className="snSearch"
            options={snOptions}
            value={snQuery}
            onChange={setSnQuery}
            onSelect={setSnQuery}
          >
            <Input.Search placeholder="Search SN" />
          </AutoComplete>
        </div>
      </div>
      <Table
        components={{
          header: {
            row: (rowProps) => <tr {...rowProps} className="tableHeaderRow" />,
          },
        }}
        className="devicesTable"
        columns={columns}
        dataSource={dataSource}
        rowKey="sn"
        pagination={{
          pageSize,
          current: page,
          onChange: setPage,
          showSizeChanger: false,
          showLessItems: true,
          showQuickJumper: false,
          showPrevNextJumpers: false,
          showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total}`,
          itemRender: (page, type, element) => {
            if (type === 'prev') return null;
            if (type === 'next') {
              return (
                <button className="ant-pagination-item ant-pagination-item-ellipsis pagination-next">
                  Next
                </button>
              );
            }
            return element;
          },
        }}
        rowClassName={() => 'uniformRow'}
      />
    </div>
  );
};

export default DevicesTable;
